import { openExternalUrl } from "./openExternalUrl";

/** Placeholder copy until Phase 2 approvals / activity tracking ship. */
export const CLIENT_WORKSPACE_PLACEHOLDER = "—";

const DEFAULT_WORKSPACE_BASE_URL = "http://localhost:3000";

/**
 * Base URL for the Client Workspace app (no trailing slash).
 * @param {NodeJS.ProcessEnv | Record<string, string | undefined>} [env]
 */
export function getClientWorkspaceBaseUrl(env = process.env) {
  const raw = String(env.REACT_APP_CLIENT_WORKSPACE_URL || DEFAULT_WORKSPACE_BASE_URL).trim();
  return raw.replace(/\/$/, "") || DEFAULT_WORKSPACE_BASE_URL;
}

/**
 * Mirror of public.ws_slugify for offline/unit use.
 * Prefer `rpc('ws_slugify')` when a Supabase client is available.
 */
export function slugifyWorkspaceName(inputName) {
  return String(inputName || "item")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function buildClientWorkspacePath(slug) {
  if (!slug) {
    return "/";
  }
  return `/${encodeURIComponent(String(slug))}`;
}

/**
 * Staff handoff URL: passes the current Supabase session via hash fragment
 * (not sent to the server) so Nexora users skip magic-link login.
 */
export function buildClientWorkspaceStaffHandoffUrl({
  baseUrl,
  slug,
  accessToken,
  refreshToken,
}) {
  const base = String(baseUrl || getClientWorkspaceBaseUrl()).replace(/\/$/, "");
  const next = buildClientWorkspacePath(slug);
  const hash = new URLSearchParams({
    access_token: accessToken,
    refresh_token: refreshToken,
    next,
  }).toString();
  return `${base}/auth/crewpilot#${hash}`;
}

export function isWorkspaceProvisioned(space) {
  return Boolean(space?.id);
}

export function isWorkspaceActive(space) {
  return space?.status === "active";
}

/** Button label for the Edit Client launcher — provisioned vs not. */
export function getClientWorkspaceLaunchLabel(space) {
  return isWorkspaceProvisioned(space)
    ? "Open Client Workspace"
    : "Create Client Workspace";
}

/**
 * Server-backed gate: authenticated user must have an active membership in the
 * Nexora operations organization (crewpilot_app_settings — not client-mutable).
 * Fail-closed on errors or missing session.
 */
export async function actorHasClientWorkspaceAccess(client) {
  if (!client?.rpc) {
    return false;
  }

  try {
    const { data, error } = await client.rpc(
      "cp_actor_has_client_workspace_access",
    );
    if (error) {
      console.error("[ClientWorkspace] access check failed:", error);
      return false;
    }
    return data === true;
  } catch (err) {
    console.error("[ClientWorkspace] access check failed:", err);
    return false;
  }
}

/**
 * Load the 1:1 workspace_spaces row for a CrewPilot client plus project count.
 * Does not duplicate CRM fields — only workspace linkage metadata.
 */
export async function loadClientWorkspaceSummary(client, clientId) {
  if (!client || !clientId) {
    return { space: null, projectCount: 0 };
  }

  const { data: space, error } = await client
    .from("workspace_spaces")
    .select(
      "id, organization_id, client_id, slug, display_name, status, journey_stage",
    )
    .eq("client_id", clientId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!space) {
    return { space: null, projectCount: 0 };
  }

  const { count, error: countError } = await client
    .from("workspace_engagements")
    .select("id", { count: "exact", head: true })
    .eq("workspace_space_id", space.id)
    .is("archived_at", null);

  if (countError) {
    throw countError;
  }

  return { space, projectCount: count ?? 0 };
}

/**
 * Provision a workspace_spaces row linked to an existing clients.client_id.
 *
 * Uses SECURITY DEFINER RPC `cp_provision_client_workspace`, which:
 * - authorizes Nexora operations owner/admin (or system owner)
 * - stamps organization_id to the locked ops org pointer
 * - requires the CRM client to belong to that same org (tenant isolation)
 * - returns the row without depending on CW `ws_is_agency_staff` INSERT RLS
 *
 * `explicitOrganizationId` is accepted for call-site compatibility but ignored;
 * the server always uses the platform ops org.
 */
export async function provisionClientWorkspace(
  client,
  actor,
  { clientId, displayName, organizationId: explicitOrganizationId } = {},
) {
  if (!client || !clientId) {
    throw new Error("Client id is required to enable a workspace.");
  }

  // actor / explicitOrganizationId are call-site context only; auth is RPC-based.
  void actor;
  void explicitOrganizationId;

  const allowed = await actorHasClientWorkspaceAccess(client);
  if (!allowed) {
    throw new Error("Client Workspace is only available to Nexora Systems.");
  }

  const name = String(displayName || "Client Workspace").trim() || "Client Workspace";

  const { data, error } = await client.rpc("cp_provision_client_workspace", {
    p_client_id: clientId,
    p_display_name: name,
  });

  if (error) {
    throw error;
  }

  if (!data?.id) {
    throw new Error("Unable to create client workspace.");
  }

  return data;
}

/**
 * Open Client Workspace by reusing the current CrewPilot Supabase session.
 * Tokens travel in the URL hash only (never sent to the CW origin as a request).
 * Does not call the access RPC — caller is responsible for UI visibility.
 *
 * @param {object} authClient Supabase client with auth.getSession
 * @param {{ slug?: string | null, openUrl?: Function, getSession?: Function, baseUrl?: string }} [options]
 */
export async function openClientWorkspaceStaffSession(
  authClient,
  {
    slug = null,
    openUrl = openExternalUrl,
    getSession = () => authClient.auth.getSession(),
    baseUrl = getClientWorkspaceBaseUrl(),
  } = {},
) {
  if (!authClient?.auth?.getSession && !getSession) {
    return false;
  }

  const {
    data: { session },
  } = await getSession();

  if (session?.access_token && session?.refresh_token) {
    return openUrl(
      buildClientWorkspaceStaffHandoffUrl({
        baseUrl,
        slug,
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
      }),
    );
  }

  // No live session — fall through to magic-link login (direct CW visitors).
  const next = encodeURIComponent(buildClientWorkspacePath(slug));
  return openUrl(`${String(baseUrl).replace(/\/$/, "")}/login?next=${next}`);
}

/**
 * Open the linked Client Workspace for a provisioned space (staff handoff).
 * Visibility should already be gated by the caller; this path does not call the access RPC.
 */
export async function openLinkedClientWorkspace(
  authClient,
  space,
  options = {},
) {
  if (!space?.slug) {
    return false;
  }

  return openClientWorkspaceStaffSession(authClient, {
    ...options,
    slug: space.slug,
  });
}
